import { useCallback, useEffect, useRef, useState } from "react";
import type { EditableMetricController } from "../../../patterns/metric-card";

export type HistoryFieldSubmitCallbacks = {
	onSuccess: () => void;
	onError: (error: Error) => void;
};

type FieldState<T> =
	| { kind: "display"; value: T }
	| { kind: "editing"; draft: T }
	| { kind: "submitting"; draft: T };

type Options<T> = {
	value: T;
	/**
	 * Persists the draft. Injected by the caller so this hook stays mutation-agnostic —
	 * the caller merges the field into its full request and fires the mutation, routing
	 * the result back through the provided callbacks.
	 */
	submit: (draft: T, callbacks: HistoryFieldSubmitCallbacks) => void;
	/** Gates the confirm icon while editing. Defaults to always-submittable. */
	canSubmit?: (draft: T) => boolean;
};

export type UseEditableHistoryFieldResult<T> = {
	controller: EditableMetricController;
	/** The value to render — the persisted value in display mode, the in-flight draft otherwise. */
	draft: T;
	changeValue: (value: T) => void;
};

/**
 * Per-card edit controller for the history/capacity metric cards, modeled on
 * {@link useEditableBLOCSize}. Owns its own `mutationError` so a mutation shared
 * across sibling cards never fans the same error/toast across all of them.
 */
export const useEditableHistoryField = <T>({
	value,
	submit: submitField,
	canSubmit,
}: Options<T>): UseEditableHistoryFieldResult<T> => {
	const [state, setState] = useState<FieldState<T>>(() => ({ kind: "display", value }));
	const [mutationError, setMutationError] = useState<Error | null>(null);
	/**
	 * Identifies the latest submission. Cancelling (or starting a new submit) bumps this so
	 * a stale in-flight callback can no-op instead of clobbering state the user has moved on from.
	 */
	const submissionRef = useRef(0);

	useEffect(() => {
		setState((current) => (current.kind === "display" ? { kind: "display", value } : current));
	}, [value]);

	const startEditing = useCallback(() => {
		setMutationError(null);
		setState({ kind: "editing", draft: value });
	}, [value]);

	const cancelEditing = useCallback(() => {
		submissionRef.current++;
		setMutationError(null);
		setState({ kind: "display", value });
	}, [value]);

	const changeValue = useCallback((next: T) => {
		setState((current) =>
			current.kind === "editing" ? { kind: "editing", draft: next } : current,
		);
	}, []);

	const submit = useCallback(() => {
		if (state.kind !== "editing") return;
		const { draft } = state;
		if (canSubmit && !canSubmit(draft)) return;
		const submissionId = ++submissionRef.current;
		setMutationError(null);
		setState({ kind: "submitting", draft });
		submitField(draft, {
			onSuccess: () => {
				if (submissionRef.current !== submissionId) return;
				setState({ kind: "display", value: draft });
			},
			onError: (error) => {
				if (submissionRef.current !== submissionId) return;
				setMutationError(error);
				setState({ kind: "editing", draft });
			},
		});
	}, [state, submitField, canSubmit]);

	const dismissError = useCallback(() => setMutationError(null), []);

	const draft = state.kind === "display" ? state.value : state.draft;
	const computedCanSubmit =
		state.kind === "editing" ? (canSubmit ? canSubmit(state.draft) : true) : false;

	return {
		controller: {
			isEditing: state.kind !== "display",
			canSubmit: computedCanSubmit,
			canCancel: state.kind === "editing",
			mutationError,
			startEditing,
			cancelEditing,
			submit,
			dismissError,
		},
		draft,
		changeValue,
	};
};
